import { useEffect, useMemo, useState } from "react";
import { ScrollView } from "react-native";
import { useTranslation } from "react-i18next";
import {
  Session,
  programWeeklyPrescription,
  type BodyFigure,
  type Program,
  type Session as SessionT,
  type UnitsPreference,
} from "@jackedlogs/shared";
import {
  ProgressRecordsCard,
  ProgressView,
  computeSessionSummary,
  computeWeeklyCounts,
  resolveTheme,
  useCatalogItems,
  useTagVolumeRecords,
  usePrograms,
  type ProgressDeps,
  type SessionSummary,
} from "@jackedlogs/ui";
import { apiFetch } from "./api";
import { durableWrite } from "./durableWrite";
import { useProfileDoc } from "./useProfileDoc";
import { useSelfProgressDeps } from "./progress/progressDeps";
import { useThemePalette } from "./theme";

// THE Progress surface on web-rnw — the SAME shared ProgressView mobile's
// Progress tab mounts (Tier-1 alerts + supporting panels + Tier-2 metrics +
// session stats + frequency chart). The panels self-fetch through apiFetch.
// The volume-by-tag records card is now the SAME shared card mobile mounts
// (lift/progress-tag-volume-web — MiniChart moved to packages/ui/src/charts,
// unblocking RW-8). painMap comes from useSelfProgressDeps (BodyScreen's
// PainMapPicker writes it, lifted 2026-08-16) — the pain panel + alert
// escalation now see real data. See next/rnw-strangler.md.

// Defer the heavy chart-workouts fetch off the initial-paint frame — it
// competes with useProgressSummary/useCatalogItems/usePrograms for the same
// mount, and the card it feeds starts collapsed (may never be opened).
// Mobile's twin defers via InteractionManager; the browser equivalent is
// requestIdleCallback, falling back to a macrotask where it's unavailable
// (Safari/older WebKit-based RNW hosts).
const scheduleIdle = (run: () => void): (() => void) => {
  if (typeof requestIdleCallback === "function") {
    const id = requestIdleCallback(run);
    return () => cancelIdleCallback(id);
  }
  const id = setTimeout(run, 0);
  return () => clearTimeout(id);
};

const formatDate = (iso: string, locale: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
  }).format(d);
};

// Session summary (total / 7d / 30d / last + weekly frequency) from the
// /api/sessions page the mobile mount also reads. Web has no offline cache
// here yet → straight to the network.
function useProgressSummary(): {
  summary: SessionSummary | null;
  weeklyCounts: number[];
  error: string | null;
} {
  const [sessions, setSessions] = useState<SessionT[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiFetch<{ items?: unknown[] }>(
          "/api/sessions?limit=200",
        );
        if (cancelled) return;
        const parsed: SessionT[] = [];
        for (const raw of res.items ?? []) {
          const p = Session.safeParse(raw);
          if (p.success) parsed.push(p.data);
        }
        setSessions(parsed);
      } catch (e) {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "load_failed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const summary = useMemo(
    () => (sessions === null ? null : computeSessionSummary(sessions, Date.now())),
    [sessions],
  );
  const weeklyCounts = useMemo(
    () => (sessions === null ? [] : computeWeeklyCounts(sessions, Date.now())),
    [sessions],
  );
  return { summary, weeklyCounts, error };
}

export function ProgressScreen() {
  const { dark, family } = useThemePalette();
  const { t, i18n } = useTranslation();
  const theme = resolveTheme(dark, family);
  const { summary, weeklyCounts, error } = useProgressSummary();
  // Catalog (slug → muscle) for the coverage hero's programWeeklyPrescription —
  // the shared picker hook, straight to network here (no cache passed) like the
  // rest of web Progress. Was a bespoke useProgressBodyMapData before the
  // bodymap panel was retired (§13.1); we reuse the primitive rather than
  // re-mint a one-off catalog fetch.
  const { raw: catalog } = useCatalogItems({ apiFetch });
  const { doc } = useProfileDoc();
  const figure = doc?.["preferredFigure"] as BodyFigure | undefined;

  // PG-1 muscle coverage — the pinned "active block" + its per-muscle weekly
  // prescription, so the coverage hero scores against real targets instead of
  // degrading to neutral bars. Mirrors the mobile ProgressScreen wiring: the
  // followed programs resolve currentProgramId → the pinned Program, and the
  // full catalog (useProgressCatalog) feeds programWeeklyPrescription.
  const currentProgramId = doc?.["currentProgramId"] as string | undefined;
  const programsState = usePrograms({ apiFetch, durableWrite });
  const pinnedProgram = useMemo<Program | null>(
    () =>
      currentProgramId
        ? (programsState.followedPrograms.find(
            (p) => p.id === currentProgramId,
          ) ?? null)
        : null,
    [programsState.followedPrograms, currentProgramId],
  );
  const prescription = useMemo(
    () =>
      pinnedProgram && catalog
        ? programWeeklyPrescription(pinnedProgram, catalog)
        : null,
    [pinnedProgram, catalog],
  );

  // The shared self base (apiFetch, theme, units, resultsCache, isDark,
  // weekStartDay) + the PG-1 extras this surface computes on top.
  const base = useSelfProgressDeps();
  const deps = useMemo<ProgressDeps>(
    () => ({ ...base, pinnedProgram, prescription, figure }),
    [base, pinnedProgram, prescription, figure],
  );

  // Volume-by-tag records card — the SAME shared card + data hook mobile
  // Progress mounts (lift/progress-tag-volume-web). Web has no offline
  // cache to protect, but the fetch still competes with everything else on
  // this mount, so it's deferred the browser way (scheduleIdle) instead of
  // mobile's InteractionManager.
  const {
    chartWorkouts,
    chartError,
    chartLoading,
    tagSeries,
    visibleTagSeries,
    tagFilters,
    setTagFilters,
    localizeTag,
  } = useTagVolumeRecords({
    apiFetch,
    t,
    resolvedLanguage: i18n.resolvedLanguage,
    schedule: scheduleIdle,
  });
  const displayUnit =
    (doc?.["unitsPreference"] as UnitsPreference | undefined) ?? "kg";

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: theme.palette.bg }}
      contentContainerStyle={{ padding: 16, gap: 16 }}
    >
      <ProgressView
        deps={deps}
        summary={summary}
        weeklyCounts={weeklyCounts}
        summaryError={error}
        formatDate={formatDate}
        locale={i18n.language}
        recordsSlot={
          <ProgressRecordsCard
            chartLoading={chartLoading}
            chartWorkouts={chartWorkouts}
            chartError={chartError}
            tagSeries={tagSeries}
            visibleTagSeries={visibleTagSeries}
            tagFilters={tagFilters}
            setTagFilters={setTagFilters}
            localizeTag={localizeTag}
            displayUnit={displayUnit}
            locale={i18n.language}
            theme={theme}
          />
        }
      />
    </ScrollView>
  );
}
